import { Fragment, MouseEvent, RefObject, useState } from 'react';
import { Close, Photo } from '@mui/icons-material';
import { Button, Modal, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useDOMRendererScreenshot from 'src/infrastructure/image/useDOMRendererScreenshot';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { useAppDispatch } from 'src/redux-app-hooks';

/** Pixels trimmed off each edge of the captured element (e.g. to cut map controls). */
export interface MapSnapshotCrop {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
}

export interface MapSnapshotButtonProps {
    /** The element to photograph — the map container (a Google Map rendered with `renderingType: RASTER`). */
    targetRef: RefObject<HTMLElement | null>;
    /** Button label — the PV Calculator and the Steckbrief both use "Save map image". */
    label: string;
    disabled?: boolean;
    /** Tooltip explaining why the button is disabled; shown only while disabled. */
    disabledTooltip?: string;
    crop?: MapSnapshotCrop;
    /**
     * Called before the capture, e.g. to hide overlays that must not appear in
     * the photo. The capture waits {@link settleMs} afterwards so the map can
     * re-render.
     */
    onBeforeCapture?: () => void;
    /** Called after the capture — ALWAYS, also when it failed (undo {@link onBeforeCapture} here). */
    onAfterCapture?: () => void;
    /**
     * Receives the captured canvas. Returns the image to show in the preview
     * modal (a data URL / object URL), or nothing to skip the preview. A thrown
     * error is reported as a snackbar.
     */
    onCapture: (canvas: HTMLCanvasElement) => Promise<string | null | void> | string | null | void;
    /** Settle time between {@link onBeforeCapture} and the capture (300 ms: the Firefox fix of PV #492). */
    settleMs?: number;
    className?: string;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * "Save map image": photographs a map container and hands the canvas to the
 * caller, then shows a preview of the result. Shared by the PV Calculator
 * (PNG blob, sent with the variant save) and the EnSan Steckbrief (the masked
 * building photo, GEN-778).
 *
 * The capture is html2canvas ({@link useDOMRendererScreenshot}), which reads
 * cross-origin map tiles with `useCORS` — but only from a RASTER Google Map: a
 * VECTOR map draws into a WebGL canvas html2canvas cannot read (the PV "map
 * screenshot" fixes #690/#691). Callers must render their map with
 * `renderingType: 'RASTER'`.
 */
export default function MapSnapshotButton({
    targetRef,
    label,
    disabled = false,
    disabledTooltip,
    crop,
    onBeforeCapture,
    onAfterCapture,
    onCapture,
    settleMs = 300,
    className,
}: MapSnapshotButtonProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const screenshot = useDOMRendererScreenshot();

    const [isLoading, setIsLoading] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);

    async function handleClick(event: MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const target = targetRef.current;
        if (!target) return;
        setIsLoading(true);
        setPreview(null);
        onBeforeCapture?.();
        try {
            // Give the map time to apply onBeforeCapture before it is photographed.
            await wait(settleMs);
            const canvas = await screenshot.screenshot(target, {
                top: crop?.top ?? 0,
                left: crop?.left ?? 0,
                right: crop?.right ?? 0,
                bottom: crop?.bottom ?? 0,
                useCORS: true,
            });
            const previewSrc = await onCapture(canvas);
            if (previewSrc) setPreview(previewSrc);
        } catch (error) {
            console.warn('MapSnapshotButton', 'Could not save the map image', error);
            dispatch(
                showSnackbar({
                    message: t('Could not save the image: {{error}}', { error: String(error) }),
                    severity: NotificationSeverity.Error,
                }),
            );
        } finally {
            onAfterCapture?.();
            setIsLoading(false);
        }
    }

    return (
        <Fragment>
            <Tooltip describeChild title={disabledTooltip ?? ''} disableHoverListener={!disabled || !disabledTooltip}>
                <span>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleClick}
                        startIcon={<Photo />}
                        className={className}
                        loading={isLoading}
                        disabled={disabled}
                    >
                        {label}
                    </Button>
                </span>
            </Tooltip>

            <Modal
                aria-labelledby="map-snapshot-preview-title"
                open={preview !== null}
                onClose={() => setPreview(null)}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <div
                    style={{
                        backgroundColor: 'white',
                        maxWidth: 500,
                        borderRadius: 8,
                        padding: 24,
                        position: 'relative',
                    }}
                >
                    <Button
                        onClick={() => setPreview(null)}
                        aria-label={t('Close')}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </Button>
                    <Typography variant="h5" id="map-snapshot-preview-title" gutterBottom>
                        {t('Image Preview')}
                    </Typography>

                    {preview && <img src={preview} alt={t('Image Preview')} width={400} />}
                </div>
            </Modal>
        </Fragment>
    );
}
